'use client';

/**
 * VOX - napojenie nasho enginu na Juliusovo rozhranie.
 *
 * Julius definoval v features/vox/types.ts kontrakt `VoxEngine` a rozhranie
 * na neho iba siaha - nevie nic o mikrofone, prepise ani o modeli. Demo engine
 * (lib/useVoxDemo.ts) prehravalo skriptovany rozhovor. Tento adapter dava na to
 * iste rozhranie nas skutocny engine z engine/useVoxCore.ts.
 *
 * Vymena je jednoriadkova: vo VoxStage.tsx `useVoxDemo()` -> `useVoxEngine()`.
 *
 * Adapter zamerne NEROBI nic viac, nez prekladá tvary dat. Ked treba zmenit
 * spravanie dialogu, meni sa engine; ked vzhlad, meni sa Juliusov komponent.
 */

import { useCallback, useMemo } from 'react';
import { useVox } from '../engine/useVoxCore';
import { useVoxRealtime } from '../engine/useVoxRealtime';

/**
 * Ktory engine bezi.
 *
 * 'realtime' - rec na rec cez WebRTC, odozva pod sekundu, da sa skakat do reci.
 * 'davkovy'  - povodny retazec nahraj/prepis/rozmysli/nahovor, hluche miesto ~4,8 s.
 *
 * Davkovy zostava v kode zamerne: je lacnejsi a keby sa v realnom case objavil
 * problem, prepnutie je jedno slovo.
 */
const ENGINE: 'realtime' | 'davkovy' = 'realtime';
import { VOX_CRITERIA_LABELS, type VoxCriteria } from '../engine/coreTypes';
import type { VoxCriterion, VoxEngine, VoxFacility, VoxFacilityKind, VoxListing, VoxMessage } from '../types';

const TYP_NAZOV: Record<string, string> = { APARTMENT: 'Byt', HOUSE: 'Dom', LAND: 'Pozemok' };

/** Nase kategorie okolia -> kategorie, ktore pozna Juliusova mapa. */
const KATEGORIE: Record<string, VoxFacilityKind> = {
    skola: 'skola',
    skolka: 'skolka',
    mhd: 'mhd',
    obchod: 'obchod',
    park: 'park',
    zdravie: 'lekar',
};

const fmt = (n: number) => new Intl.NumberFormat('sk-SK').format(n);

const KAT_NAZOV: Record<string, string> = {
    APARTMENT: 'byt',
    HOUSE: 'dom',
    RECREATIONAL: 'rekreačné',
    LAND: 'pozemok',
    OTHER: 'komerčné',
};
const DISPOZICIA: Record<string, string> = {
    STUDIO: 'garsónka',
    ONE_OR_ONE_HALF: '1-izbový',
    TWO_OR_TWO_HALF: '2-izbový',
    THREE_OR_THREE_HALF: '3-izbový',
    FOUR_OR_FOUR_HALF: '4-izbový',
    FIVE_OR_MORE: '5 a viac izbový',
};
const STAV: Record<string, string> = {
    NEW_BUILD: 'novostavba',
    FULL_RECONSTRUCTION: 'úplná rekonštrukcia',
    PARTIAL_RECONSTRUCTION: 'čiastočná rekonštrukcia',
    ORIGINAL: 'pôvodný stav',
    UNDER_CONSTRUCTION: 'vo výstavbe',
};
const PRIESTORY: Record<string, string> = {
    BALCONY: 'balkón',
    LOGGIA: 'loggia',
    TERRACE: 'terasa',
    CELLAR: 'pivnica',
    GARAGE: 'garáž',
    PARKING: 'parkovanie',
};
const MEDIA: Record<string, string> = {
    HAS_PHOTOS: 's fotkami',
    HAS_VIDEO: 's videom',
    VIRTUAL_TOUR: '3D obhliadka',
};

/** Rozsah {min,max} do textu chipu. */
function rozsah(v: { min?: number | null; max?: number | null } | null | undefined, jednotka: string) {
    if (!v) return '';
    const min = v.min != null ? fmt(v.min) : null;
    const max = v.max != null ? fmt(v.max) : null;
    if (min && max) return min + ' - ' + max + ' ' + jednotka;
    if (max) return 'do ' + max + ' ' + jednotka;
    if (min) return 'od ' + min + ' ' + jednotka;
    return '';
}

const zoznam = (v: unknown, slovnik: Record<string, string>) =>
    Array.isArray(v) ? v.map((x) => slovnik[String(x)] ?? String(x)).join(', ') : '';

/** Hodnota kriteria do textu chipu. */
function hodnota(key: keyof VoxCriteria, v: unknown): string {
    switch (key) {
        case 'transactionType':
            return v === 'RENT' ? 'prenájom' : 'predaj';
        case 'propertyCategory':
            return zoznam(v, KAT_NAZOV);
        case 'roomCategory':
            return zoznam(v, DISPOZICIA);
        case 'propertyCondition':
            return zoznam(v, STAV);
        case 'additionalSpaces':
            return zoznam(v, PRIESTORY);
        case 'media':
            return zoznam(v, MEDIA);
        case 'location': {
            const l = v as VoxCriteria['location'];
            const mena = (l?.municipalityName || []).join(', ');
            return mena + (l?.radiusKm ? ' + ' + l.radiusKm + ' km okolie' : '');
        }
        case 'price':
            return rozsah(v as VoxCriteria['price'], '€');
        case 'usableArea':
        case 'landArea':
            return rozsah(v as VoxCriteria['usableArea'], 'm2');
        case 'roomCount':
            return rozsah(v as VoxCriteria['roomCount'], 'izieb');
        case 'features': {
            const f = v as VoxCriteria['features'];
            return (f?.equipment || []).join(', ');
        }
        default:
            return String(v);
    }
}

/**
 * Ako dobre ponuka sedi na rozhovor, 0-100.
 *
 * Vsetky ponuky vo vysledku uz kriteria splnaju - vyhladavanie ich inak nevrati.
 * Preto sa neodmenuje splnenie, ale to, ako pohodlne su splnene: cena hlboko pod
 * rozpoctom a vymera nad poziadavkou. Bez toho by mali vsetky rovnakych 100 %
 * a cislo by nic nehovorilo.
 */
function zhoda(l: { price: number; area: number | null }, c: VoxCriteria): number {
    let skore = 72;

    const strop = c.price?.max;
    if (strop && l.price) {
        const rezerva = (strop - l.price) / strop; // 0 = presne na hranici rozpoctu
        skore += Math.max(-6, Math.min(18, Math.round(rezerva * 60)));
    }
    const minPlocha = c.usableArea?.min;
    if (minPlocha && l.area) {
        const navyse = (l.area - minPlocha) / minPlocha;
        skore += Math.max(-4, Math.min(10, Math.round(navyse * 40)));
    }
    return Math.max(40, Math.min(99, skore));
}

/** Kontrakt plus spektrum navyse - rozhranie ho pouziva na vlnu v tlacidle. */
export type VoxEngineSoVlnou = VoxEngine & { getBars: () => number[] };

export function useVoxEngine(): VoxEngineSoVlnou {
    // Oba hooky sa musia zavolat vzdy - React nedovoluje volat hook podmienene.
    // Nepouzity engine nic nerobi, kym sa nespusti (mikrofon otvara az start).
    const realtime = useVoxRealtime();
    const davkovy = useVox();
    const core = ENGINE === 'realtime' ? realtime : davkovy;

    // Stabilne id sprav - rozhranie ich pouziva ako React key.
    //
    // Odvodzujeme ich z poradia a roly, nie z pocitadla v refe: spravy sa do
    // zoznamu iba pridavaju na koniec, takze index je stabilny, a citanie refu
    // pocas vykreslovania React zakazuje.
    const messages: VoxMessage[] = useMemo(
        () =>
            core.messages.map((m, i) => ({
                id: 'm' + i + '-' + m.role,
                // Nase systemove poznamky ("odvodene: dve deti -> 4 izby") patria
                // do transkriptu ako hlas asistenta - su to jeho vysvetlenia.
                role: m.role === 'user' ? 'user' : 'realico',
                text: m.content,
            })),
        [core.messages],
    );

    const criteria: VoxCriterion[] = useMemo(
        () =>
            core.activeKeys.map((k) => ({
                key: String(k),
                label: VOX_CRITERIA_LABELS[k] + ': ' + hodnota(k, core.criteria[k]),
            })),
        [core.activeKeys, core.criteria],
    );

    const results: VoxListing[] = useMemo(
        () =>
            core.items.map((l) => ({
                id: String(l.id ?? l.code),
                slug: String(l.code),
                title:
                    (TYP_NAZOV[l.property_type] || 'Nehnuteľnosť') +
                    (l.txn_type === 'RENT' ? ' na prenájom' : ' na predaj') +
                    (l.street ? ', ' + l.street : ''),
                locality: [l.city, l.street].filter(Boolean).join(', '),
                price: l.price,
                area: l.area ?? 0,
                rooms: undefined,
                // Nahlad, nie plna fotka - v pase sa zobrazuje 96 px siroka
                photo: l.photos && l.photos.length ? '/vox-api/photos/listings/thumb/' + l.photos[0] : '',
                lat: Number(l.lat ?? 0),
                lng: Number(l.lng ?? 0),
                match: zhoda(l, core.criteria),
            })),
        [core.items, core.criteria],
    );

    /**
     * Okolie prvej ponuky. Rozhranie caka jeden zoznam pre mapu, nie zoznam
     * na kazdu ponuku - preto sa berie okolie titulnej ponuky vo vysledku.
     */
    const facilities: VoxFacility[] = useMemo(() => {
        const prva = core.items.find((x) => x.nearby && x.nearby.okolie);
        if (!prva || !prva.nearby) return [];
        const out: VoxFacility[] = [];
        Object.entries(prva.nearby.okolie).forEach(([kat, data]) => {
            const kind = KATEGORIE[kat];
            if (!kind || !data || !Array.isArray(data.najblizsie)) return;
            data.najblizsie.slice(0, 3).forEach((f, i) => {
                if (typeof f.lat !== 'number' || typeof f.lng !== 'number') return;
                out.push({
                    id: kat + '-' + i,
                    kind,
                    name: f.nazov || data.label,
                    lat: f.lat,
                    lng: f.lng,
                    distance: f.m,
                });
            });
        });
        return out;
    }, [core.items]);

    const sendText = useCallback(
        (text: string) => {
            void core.ask(text);
        },
        [core],
    );

    return {
        phase: core.mode,
        isActive: core.started,
        messages,
        criteria,
        results,
        facilities,
        start: core.toggleMic,
        stop: core.stop,
        sendText,
        reset: core.reset,
        getLevel: core.getLevel,
        getBars: core.getBars,
    };
}
